/**
 * Consent-respecting click events for the marketing → signup/checkout path.
 *
 * Each helper fires the same event name to GA4 (`trackEvent`) and PostHog
 * (`capturePostHogEvent`). Both wrappers no-op without accepted analytics
 * consent, during SSR, or before the SDK is ready, so callers can invoke them
 * unconditionally from a click handler.
 *
 * `route` is the marketing page the click happened on (pathname only — never
 * the query string). On a suppressed (token-bearing) URL it is replaced by a
 * fixed marker so no bearer path can reach a sink; see
 * docs/security/token-pii-sinks.md.
 */

import { trackEvent } from "@/lib/analytics";
import { capturePostHogEvent } from "@/lib/analytics-posthog";
import { isSuppressedUrlString } from "@/lib/analytics/suppressed-paths";

export const SUPPRESSED_ROUTE_MARKER = "suppressed";

/** Current pathname, safe for an analytics property. */
export function currentAnalyticsRoute(): string {
  if (typeof window === "undefined") return "unknown";
  const { pathname, search } = window.location;
  if (isSuppressedUrlString(`${pathname}${search}`)) {
    return SUPPRESSED_ROUTE_MARKER;
  }
  return pathname || "/";
}

function fireBoth(
  name: string,
  params: Record<string, string | number | boolean | null | undefined>,
): void {
  trackEvent(name, params);
  capturePostHogEvent(name, params);
}

export type HeaderCtaLocation = "header" | "mobile-menu";

/** Site-header "Get started" (desktop header or mobile menu). */
export function trackHeaderCtaClicked({
  ctaLocation,
  destination,
  ctaLabel,
}: {
  ctaLocation: HeaderCtaLocation;
  destination: string;
  ctaLabel: string;
}): void {
  fireBoth("header_cta_clicked", {
    route: currentAnalyticsRoute(),
    cta_location: ctaLocation,
    cta_label: ctaLabel,
    destination,
  });
}

export type UpgradeTargetPlan = "pro" | "business";
export type UpgradeBillingCycle = "monthly" | "annual";

/**
 * Public pricing/plan CTA click (`upgrade_clicked`). `ctaLocation` is the
 * surface the click came from — e.g. "pricing-card" on /pricing,
 * "business-page" on /business — so the two journeys are distinguishable.
 */
export function trackUpgradeClicked({
  ctaLocation,
  targetPlan,
  billingCycle,
}: {
  ctaLocation: string;
  targetPlan: UpgradeTargetPlan;
  billingCycle: UpgradeBillingCycle;
}): void {
  fireBoth("upgrade_clicked", {
    cta_location: ctaLocation,
    target_plan: targetPlan,
    billing_cycle: billingCycle,
  });
}

export type CheckoutPlanKey = "monthly" | "annual" | "business";

// Checkout price keys → reported plan / billing interval (display taxonomy,
// not an entitlement check).
const CHECKOUT_PLAN: Record<CheckoutPlanKey, "pro" | "business"> = {
  monthly: "pro",
  annual: "pro",
  business: "business",
};
const CHECKOUT_INTERVAL: Record<CheckoutPlanKey, "month" | "year"> = {
  monthly: "month",
  annual: "year",
  business: "month",
};

/**
 * Checkout confirmation modal "Continue" click (`checkout_continue_clicked`)
 * — the last client step before the Stripe redirect or, for a logged-out
 * visitor, the signup bounce.
 */
export function trackCheckoutContinueClicked({
  planKey,
  location,
}: {
  planKey: CheckoutPlanKey;
  location: string;
}): void {
  fireBoth("checkout_continue_clicked", {
    plan: CHECKOUT_PLAN[planKey],
    interval: CHECKOUT_INTERVAL[planKey],
    location,
    route: currentAnalyticsRoute(),
  });
}
